import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import SingleDropdown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CompanyInfoFormFlatFields } from 'src/app/Onboarding/Onboarding'
import { ReferralSources } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { postCreateOrganizationAndOpenToastAndClosePopup } from 'src/redux/actions/data/organizations.actions'
import {
  selectDepartmentOptions,
  selectIndustryOptions,
  selectReferralSourceOptions,
  selectTeamSizeOptions,
} from 'src/redux/selectors/data/config.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import colors from 'src/style/variables.json'
import { css } from 'styled-components'

interface OnboardingStepTwoProps {
  currentStep: number
  totalSteps: number
  setStep: (val: number) => void
  companyLegalName: string | undefined
  flatFields: CompanyInfoFormFlatFields
  onFlatFieldChange: (
    fieldName: keyof CompanyInfoFormFlatFields,
    value: CompanyInfoFormFlatFields[keyof CompanyInfoFormFlatFields],
  ) => void
  flatFieldsErrors: Partial<
    Record<keyof CompanyInfoFormFlatFields, Array<React.ReactElement | string>>
  >
  setErrorsAndReturnDidPassValidation: () => boolean
  setErrors: (
    errors: Partial<
      Record<
        keyof CompanyInfoFormFlatFields,
        Array<React.ReactElement | string>
      >
    >,
  ) => void
  jpm?: boolean
  testId?: string
}

const OnboardingStepTwo: React.FC<OnboardingStepTwoProps> = (props) => {
  const industryOptions = useSelector(selectIndustryOptions)
  const departmentOptions = useSelector(selectDepartmentOptions)
  const teamSizeOptions = useSelector(selectTeamSizeOptions)
  const referralSourceOptions = useSelector(selectReferralSourceOptions)
  const appName = useSelector(selectAppName)

  const { analytics } = useAnalyticsContext()
  const dispatch = useDispatch()
  const translate = useTranslate()

  // the value in this check will most likely change
  const nameInput =
    props.flatFields.referralSource === ReferralSources.bankCreditCardRep

  const onOnboardingInfoFormSubmit = (): void => {
    analytics?.track('[onboarding] company info - continue', {
      screen: '/onboarding/info',
      industry_value: props.flatFields.industry,
      team_size_value: props.flatFields.teamSize,
      user_department_value: props.flatFields.department,
      referral: props.flatFields.referralSource,
      name_input: nameInput,
    })

    const companyInfoFlatFieldsPassedValidation =
      props.setErrorsAndReturnDidPassValidation()

    if (companyInfoFlatFieldsPassedValidation) {
      const teamSizeObject = Object.values(teamSizeOptions).filter(
        (teamSize) => teamSize.details.text === props.flatFields.teamSize,
      )[0]

      dispatch(
        postCreateOrganizationAndOpenToastAndClosePopup({
          name: props.companyLegalName,
          industry: props.flatFields.industry,
          teamSizeMin: teamSizeObject.details.min,
          teamSizeMax: teamSizeObject.details.max,
          registrantCompanyDepartment: props.flatFields.department,
          referralSource: props.flatFields.referralSource,
          referralName: props.flatFields.referralName,
        }),
      )

      props.setStep(3)
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-grow: 1;
      `}
    >
      <div
        css={css`
          padding: 80px;
          flex-grow: 1;
        `}
      >
        <div
          css={css`
            ${getFontCss(
              12,
              props.jpm
                ? colors.tokensColorJPM.JPMbrown500
                : styleConstants.colors.purple,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 20px;
          `}
        >
          {translate('Step ${currentStep} of ${totalSteps}', {
            currentStep: props.currentStep.toString(),
            totalSteps: props.totalSteps.toString(),
          })}
        </div>
        <div
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 20px;
          `}
        >
          {translate('Tell us about your business')}
        </div>
        <div
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 40px;
          `}
        >
          {translate(
            'The information you enter helps us personalize your ${appName} experience.',
            { appName },
          )}
        </div>
        <SingleDropdown
          testId={'industry'}
          label={translate('Industry')}
          options={industryOptions}
          selectedValue={
            props.flatFields['industry'] !== undefined
              ? { value: props.flatFields['industry'] }
              : undefined
          }
          onSelect={({ value }) => {
            props.onFlatFieldChange('industry', value)
            props.setErrors({
              ...props.flatFieldsErrors,
              industry: undefined,
            })
          }}
          placeholder={translate('Select your industry')}
          errorText={props.flatFieldsErrors['industry']}
          requirement={'required'}
        />
        <SingleDropdown
          testId={'teamSize'}
          css={css`
            margin-top: 20px;
          `}
          label={translate('Team Size')}
          options={teamSizeOptions}
          selectedValue={
            props.flatFields['teamSize'] !== undefined
              ? { value: props.flatFields['teamSize'] }
              : undefined
          }
          onSelect={({ value }) => {
            props.onFlatFieldChange('teamSize', value)
            props.setErrors({
              ...props.flatFieldsErrors,
              teamSize: undefined,
            })
          }}
          placeholder={translate('Select your team size')}
          errorText={props.flatFieldsErrors['teamSize']}
          requirement={'required'}
        />
        <SingleDropdown
          testId={'department'}
          css={css`
            margin-top: 20px;
          `}
          label={translate('Department')}
          options={departmentOptions}
          selectedValue={
            props.flatFields['department'] !== undefined
              ? { value: props.flatFields['department'] }
              : undefined
          }
          onSelect={({ value }) => {
            props.onFlatFieldChange('department', value)
            props.setErrors({
              ...props.flatFieldsErrors,
              department: undefined,
            })
          }}
          placeholder={translate('Select your department')}
          errorText={props.flatFieldsErrors['department']}
          requirement={'required'}
        />
        <SingleDropdown
          testId={'referralSource'}
          css={css`
            margin-top: 20px;
          `}
          label={translate('How did you hear about us?')}
          options={referralSourceOptions}
          selectedValue={
            props.flatFields['referralSource'] !== undefined
              ? { value: props.flatFields['referralSource'] }
              : undefined
          }
          onSelect={({ value }) => {
            props.onFlatFieldChange('referralSource', value)
            props.setErrors({
              ...props.flatFieldsErrors,
              referralSource: undefined,
            })
          }}
          placeholder={translate('Select an option')}
          errorText={props.flatFieldsErrors['referralSource']}
          requirement={'required'}
        />
        {nameInput && (
          <TextInput
            data-testid={'referralName'}
            css={css`
              margin-top: 20px;
            `}
            label={translate('What is the name of your bank representative?')}
            value={props.flatFields['referralName']}
            onChange={(e) => {
              props.onFlatFieldChange(
                'referralName',
                e.target.value.replace(/[0-9]+/g, ''),
              )
            }}
            placeholder={translate('Enter name of the bank representative')}
            requirement={'optional'}
          />
        )}
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 40px;
            gap: 20px;
          `}
        >
          <Button
            type="button"
            testId="next-button"
            variant="primary"
            onClick={onOnboardingInfoFormSubmit}
          >
            {translate(
              props.jpm
                ? 'Next: Register Account'
                : 'Next: Register Credit Card',
            )}
          </Button>
        </div>
      </div>
      <div
        css={css`
          border-radius: 0px 10px 10px 0px;
          max-width: 320px;
          ${props.jpm
            ? css`
                background: ${colors.tokensColorJPM['JPMbrown-brand-color']};
              `
            : css`
                background: linear-gradient(
                  27deg,
                  #4f3fdd 0.27%,
                  #588af2 100.75%
                );
                box-shadow: 4px 0 8px 0 rgba(0, 0, 0, 0.15) inset;
              `};
        `}
      >
        <div
          css={css`
            margin: 120px 40px;
          `}
        >
          <div
            css={css`
              ${getFontCss(
                20,
                styleConstants.colors.white,
                styleConstants.fontFamilies.medium,
              )};
              margin-bottom: 16px;
            `}
          >
            {translate('What do we do with this information?')}
          </div>
          <div
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.white,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 16px;
            `}
          >
            {translate(
              'This information helps us deliver optimal service and develop expense management features that best suit your business.',
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingStepTwo
